/**
 * GrantList Component
 *
 * Displays grants in a searchable, filterable table matching the Grant schema structure (v5.002).
 *
 * Columns:
 * - Client: clientId (only shown for APP_ADMIN users)
 * - Grant Name: name + description
 * - Subject: subjectType (ROLE/USER) + subjectValue
 * - Resource Target: targetDomain:targetResourceTypeId:targetResourceId
 * - V/E/C/D/DA/EX: Individual action columns with vertical headers - ✓ (ALLOW), ✗ (DENY), − (implicit DENY)
 * - Priority: Conflict resolution priority (1-999)
 * - Status: isActive boolean (ACTIVE/INACTIVE)
 * - Actions Menu: Edit, Delete (3-dot menu)
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Stack,
  Menu
} from '@mui/material';
import {
  Edit,
  Delete,
  Refresh,
  Add,
  FilterList,
  Search,
  MoreVert,
  Check as CheckIcon,
  Close as CloseIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';

import AdminButton from '../../../../../components/common/AdminButton';
import GrantModal from './GrantModal';
import { formatDate } from '../utils/dateHelpers';
import { getGrantStatusColor, getGrantTypeIcon } from '../utils/grantHelpers';

/**
 * Actions Menu Component - 3-dot menu for Edit and Delete actions
 */
function ActionsMenu({ grant, onEdit, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleClose();
    onEdit(grant);
  };

  const handleDelete = () => {
    handleClose();
    if (window.confirm(`Delete grant "${grant.name}"?`)) {
      onDelete(grant.id);
    }
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        aria-label="more actions"
        aria-controls={open ? 'actions-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        id="actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'actions-button',
        }}
      >
        <MenuItem onClick={handleEdit}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}

export default function GrantList({
  grants = [],
  onDelete,
  onRefresh,
  onSave,
  user,
  selectedClient,
  availableClients = []
}) {
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: 'name', desc: false }]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [subjectTypeFilter, setSubjectTypeFilter] = useState('ALL');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGrant, setSelectedGrant] = useState(null);

  // Check if user is APP_ADMIN
  const isAppAdmin = user?.groupRole === 'APP_ADMIN';

  // Filter grants based on isActive status and subject type only
  // Client filtering is now done proactively via AdminContext
  const filteredGrants = useMemo(() => {
    let filtered = [...grants];

    if (statusFilter === 'ACTIVE') {
      filtered = filtered.filter(g => g.isActive === true);
    } else if (statusFilter === 'INACTIVE') {
      filtered = filtered.filter(g => g.isActive === false);
    }
    // 'ALL' shows both active and inactive

    if (subjectTypeFilter !== 'ALL') {
      filtered = filtered.filter(g => g.subjectType === subjectTypeFilter);
    }

    return filtered;
  }, [grants, statusFilter, subjectTypeFilter]);

  // Modal handlers
  const handleOpenCreateModal = useCallback(() => {
    setSelectedGrant(null);
    setModalOpen(true);
  }, []);

  const handleOpenEditModal = useCallback((grant) => {
    setSelectedGrant(grant);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedGrant(null);
  }, []);

  const handleSaveGrant = useCallback(async (grantData) => {
    await onSave(grantData);
    handleCloseModal();
    if (onRefresh) {
      onRefresh();
    }
  }, [onSave, onRefresh, handleCloseModal]);

  const columns = useMemo(
    () => [
      // Client ID column (only for APP_ADMIN)
      ...(isAppAdmin ? [{
        accessorKey: 'clientId',
        header: 'Client',
        cell: ({ row }) => (
          <Chip
            label={row.original.clientId}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          />
        )
      }] : []),
      {
        accessorKey: 'name',
        header: 'Grant Name',
        cell: ({ row }) => {
          const grant = row.original;
          return (
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" display="block">
                    <strong>ID:</strong> {grant.id?.slice(0, 8)}...
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Description:</strong> {grant.description || 'N/A'}
                  </Typography>
                </Box>
              }
              arrow
            >
              <Typography variant="body2" fontWeight="medium" sx={{ cursor: 'help' }}>
                {grant.name || `Grant ${grant.id?.slice(0, 8)}...`}
              </Typography>
            </Tooltip>
          );
        }
      },
      {
        accessorKey: 'subject',
        header: 'Subject',
        cell: ({ row }) => {
          const grant = row.original;
          return (
            <Stack direction="column" spacing={0.5}>
              <Chip
                label={grant.subjectType}
                size="small"
                color={grant.subjectType === 'ROLE' ? 'primary' : 'secondary'}
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
              <Typography variant="body2" fontWeight="medium">
                {grant.subjectValue}
              </Typography>
            </Stack>
          );
        }
      },
      {
        accessorKey: 'targeting',
        header: 'Resource Target',
        cell: ({ row }) => {
          const grant = row.original;
          const domain = grant.targetDomain || '*';
          const resourceType = grant.targetResourceTypeId || '*';
          const resource = grant.targetResourceId || '*';

          return (
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" display="block">
                    <strong>Domain:</strong> {domain}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Resource Type:</strong> {resourceType}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Resource ID:</strong> {resource}
                  </Typography>
                  {grant.targetResourcePattern && (
                    <Typography variant="caption" display="block" sx={{ mt: 1, fontFamily: 'monospace' }}>
                      <strong>Pattern:</strong> {grant.targetResourcePattern}
                    </Typography>
                  )}
                </Box>
              }
              arrow
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  backgroundColor: '#f5f5f5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  cursor: 'help'
                }}
              >
                {domain}:{resourceType === '*' ? '*' : resourceType.slice(0, 8)}:{resource === '*' ? '*' : resource.slice(0, 8)}
              </Typography>
            </Tooltip>
          );
        }
      },
      // Individual action columns with horizontal headers
      {
        id: 'action_VIEW',
        header: () => (
          <Tooltip title="VIEW" arrow>
            <Box sx={{ fontSize: '0.75rem', fontWeight: 600, cursor: 'help' }}>V</Box>
          </Tooltip>
        ),
        cell: ({ row }) => {
          const actionConfigs = row.original.actionConfigs || [];
          const actionConfig = actionConfigs.find(ac => ac.action === 'VIEW');
          const effect = actionConfig?.effect;

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {effect === 'ALLOW' ? (
                <CheckIcon sx={{ fontSize: 16, color: '#4caf50' }} />
              ) : effect === 'DENY' ? (
                <CloseIcon sx={{ fontSize: 16, color: '#f44336' }} />
              ) : (
                <RemoveIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
              )}
            </Box>
          );
        }
      },
      {
        id: 'action_EDIT',
        header: () => (
          <Tooltip title="EDIT" arrow>
            <Box sx={{ fontSize: '0.75rem', fontWeight: 600, cursor: 'help' }}>E</Box>
          </Tooltip>
        ),
        cell: ({ row }) => {
          const actionConfigs = row.original.actionConfigs || [];
          const actionConfig = actionConfigs.find(ac => ac.action === 'EDIT');
          const effect = actionConfig?.effect;

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {effect === 'ALLOW' ? (
                <CheckIcon sx={{ fontSize: 16, color: '#4caf50' }} />
              ) : effect === 'DENY' ? (
                <CloseIcon sx={{ fontSize: 16, color: '#f44336' }} />
              ) : (
                <RemoveIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
              )}
            </Box>
          );
        }
      },
      {
        id: 'action_CREATE',
        header: () => (
          <Tooltip title="CREATE" arrow>
            <Box sx={{ fontSize: '0.75rem', fontWeight: 600, cursor: 'help' }}>C</Box>
          </Tooltip>
        ),
        cell: ({ row }) => {
          const actionConfigs = row.original.actionConfigs || [];
          const actionConfig = actionConfigs.find(ac => ac.action === 'CREATE');
          const effect = actionConfig?.effect;

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {effect === 'ALLOW' ? (
                <CheckIcon sx={{ fontSize: 16, color: '#4caf50' }} />
              ) : effect === 'DENY' ? (
                <CloseIcon sx={{ fontSize: 16, color: '#f44336' }} />
              ) : (
                <RemoveIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
              )}
            </Box>
          );
        }
      },
      {
        id: 'action_DELETE',
        header: () => (
          <Tooltip title="DELETE" arrow>
            <Box sx={{ fontSize: '0.75rem', fontWeight: 600, cursor: 'help' }}>D</Box>
          </Tooltip>
        ),
        cell: ({ row }) => {
          const actionConfigs = row.original.actionConfigs || [];
          const actionConfig = actionConfigs.find(ac => ac.action === 'DELETE');
          const effect = actionConfig?.effect;

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {effect === 'ALLOW' ? (
                <CheckIcon sx={{ fontSize: 16, color: '#4caf50' }} />
              ) : effect === 'DENY' ? (
                <CloseIcon sx={{ fontSize: 16, color: '#f44336' }} />
              ) : (
                <RemoveIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
              )}
            </Box>
          );
        }
      },
      {
        id: 'action_DEACTIVATE',
        header: () => (
          <Tooltip title="DEACTIVATE" arrow>
            <Box sx={{ fontSize: '0.75rem', fontWeight: 600, cursor: 'help' }}>DA</Box>
          </Tooltip>
        ),
        cell: ({ row }) => {
          const actionConfigs = row.original.actionConfigs || [];
          const actionConfig = actionConfigs.find(ac => ac.action === 'DEACTIVATE');
          const effect = actionConfig?.effect;

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {effect === 'ALLOW' ? (
                <CheckIcon sx={{ fontSize: 16, color: '#4caf50' }} />
              ) : effect === 'DENY' ? (
                <CloseIcon sx={{ fontSize: 16, color: '#f44336' }} />
              ) : (
                <RemoveIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
              )}
            </Box>
          );
        }
      },
      {
        id: 'action_EXPORT',
        header: () => (
          <Tooltip title="EXPORT" arrow>
            <Box sx={{ fontSize: '0.75rem', fontWeight: 600, cursor: 'help' }}>EX</Box>
          </Tooltip>
        ),
        cell: ({ row }) => {
          const actionConfigs = row.original.actionConfigs || [];
          const actionConfig = actionConfigs.find(ac => ac.action === 'EXPORT');
          const effect = actionConfig?.effect;

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {effect === 'ALLOW' ? (
                <CheckIcon sx={{ fontSize: 16, color: '#4caf50' }} />
              ) : effect === 'DENY' ? (
                <CloseIcon sx={{ fontSize: 16, color: '#f44336' }} />
              ) : (
                <RemoveIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
              )}
            </Box>
          );
        }
      },
      {
        accessorKey: 'priority',
        header: 'Priority',
        cell: ({ row }) => (
          <Chip
            label={row.original.priority || 100}
            size="small"
            color={row.original.priority > 500 ? 'error' : row.original.priority > 250 ? 'warning' : 'default'}
            variant="outlined"
          />
        )
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <Chip
            label={row.original.isActive ? 'ACTIVE' : 'INACTIVE'}
            size="small"
            color={row.original.isActive ? 'success' : 'default'}
            variant="outlined"
          />
        )
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => <ActionsMenu grant={row.original} onEdit={handleOpenEditModal} onDelete={onDelete} />
      }
    ],
    [onDelete, handleOpenEditModal, isAppAdmin]
  );

  const table = useReactTable({
    data: filteredGrants,
    columns,
    state: {
      globalFilter,
      columnFilters,
      sorting
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10
      }
    }
  });

  return (
    <Box>
      {/* Filters and Actions */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search grants..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Status</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Subject Type</InputLabel>
            <Select
              value={subjectTypeFilter}
              label="Subject Type"
              onChange={(e) => setSubjectTypeFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Types</MenuItem>
              <MenuItem value="USER">User</MenuItem>
              <MenuItem value="ROLE">Role</MenuItem>
            </Select>
          </FormControl>

          <AdminButton
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => {
              setGlobalFilter('');
              setStatusFilter('ALL');
              setSubjectTypeFilter('ALL');
              setColumnFilters([]);
            }}
          >
            Clear
          </AdminButton>

          <AdminButton
            variant="outlined"
            startIcon={<Refresh />}
            onClick={onRefresh}
          >
            Refresh
          </AdminButton>

          <AdminButton
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreateModal}
          >
            New Grant
          </AdminButton>
        </Stack>
      </Box>

      {/* Table */}
      <Paper variant="outlined">
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        padding: '12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #e0e0e0',
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        userSelect: 'none',
                        backgroundColor: '#f5f5f5',
                        fontSize: '0.8125rem',
                        fontWeight: 600
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() && (
                        <span>{header.column.getIsSorted() === 'desc' ? ' ↓' : ' ↑'}</span>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  style={{
                    borderBottom: '1px solid #e0e0e0',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} style={{ padding: '12px' }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>

        {/* Pagination */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {table.getRowModel().rows.length} of {filteredGrants.length} grants
          </Typography>
          <Stack direction="row" spacing={1}>
            <AdminButton
              size="small"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </AdminButton>
            <AdminButton
              size="small"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </AdminButton>
          </Stack>
        </Box>
      </Paper>

      {/* Grant Modal */}
      <GrantModal
        open={modalOpen}
        onClose={handleCloseModal}
        grant={selectedGrant}
        onSave={handleSaveGrant}
        user={user}
        selectedClient={selectedClient}
        availableClients={availableClients}
      />
    </Box>
  );
}